import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, MonitorIcon, ZapIcon, ShieldIcon, SmartphoneIcon, SearchIcon, MailIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function WebDesignService() {
  const features = [{
    icon: MonitorIcon,
    title: 'Bespoke Design',
    description: 'Custom layouts tailored to your brand'
  }, {
    icon: ZapIcon,
    title: 'Lightning Fast',
    description: 'Optimised for speed and performance'
  }, {
    icon: SmartphoneIcon,
    title: 'Mobile-First',
    description: 'Perfect on all devices and screen sizes'
  }, {
    icon: ShieldIcon,
    title: 'Secure & Safe',
    description: 'SSL certificates and security best practices'
  }, {
    icon: SearchIcon,
    title: 'SEO Ready',
    description: 'Built to rank well in search engines'
  }, {
    icon: MailIcon,
    title: 'Contact Forms',
    description: 'Integrated forms with spam protection'
  }];
  const benefits = ['Own your website 100% - no lock-ins', 'Free domain & SSL for the first year', '3 months free hosting included', 'Easy-to-use content management', 'Training & documentation provided', '30-day support after launch'];
  const faqs = [{
    question: 'How long does it take to build a website?',
    answer: 'Typically 2-4 weeks depending on complexity. We will provide a detailed timeline during consultation.'
  }, {
    question: 'Will my website work on mobile devices?',
    answer: 'Yes, all our websites are fully responsive and optimised for mobile, tablet, and desktop.'
  }, {
    question: 'Can I update the content myself?',
    answer: 'Absolutely. We provide CMS training so you can easily update text, images, and pages.'
  }, {
    question: 'Do you provide ongoing support?',
    answer: 'Yes, we offer maintenance plans with updates, security monitoring, and technical support.'
  }];
  return <>
      <Helmet data-id="element-2197">
        <title data-id="element-2198">Web Design | Bright Pixxel Studios – UK</title>
        <meta name="description" content="Web Design for UK businesses. Fast, mobile-ready and SEO-optimised. Get a free quote." data-id="element-2199" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2201">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2202">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2203">
              <MonitorIcon size={40} className="text-[#0DC3DC]" data-id="element-2204" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2205">
              Professional Web Design
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2206">
              Beautiful, fast websites that work perfectly on every device and
              help your business grow.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2207">
              <Link to="/pricing" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-2208">
                Get a Free Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-2209">
                See Portfolio
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2210">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2211">
          <div className="max-w-6xl mx-auto" data-id="element-2212">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-2213">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2214">
                Everything You Need
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2215">
                Professional features included in every website
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2216">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-2217">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2218">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-2219" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-2220">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-2221">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2222">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2223">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2224">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2225">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2226">
                Why Choose Us?
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2227">
                We build websites that you own completely, with no hidden fees
                or ongoing commitments.
              </p>
              <div className="space-y-4" data-id="element-2228">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2229">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2230">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2231" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2232">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2233">
              <img src="https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Web design workspace with laptop and design tools - Bright Pixxel Studios professional web development" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2234" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2235">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2236">
          <div className="max-w-3xl mx-auto" data-id="element-2237">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2238">
              Common Questions
            </motion.h2>
            <div className="space-y-6" data-id="element-2239">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl" data-id="element-2240">
                  <h3 className="text-lg font-bold mb-3 flex items-start gap-3" data-id="element-2241">
                    <span className="text-[#0DC3DC] text-2xl font-bold flex-shrink-0" data-id="element-2242">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 pl-9" data-id="element-2243">
                    {faq.answer}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2244">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2245">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2246">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2247">
              Ready to Get Started?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2248">
              Get in touch today for a free consultation and quote.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2249">
              <a href="mailto:info@bright-pixxel.com?subject=New%20Enquiry%20-%20Web%20Design" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg text-lg" data-id="element-2250">
                Get Your Free Quote
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}